import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Check, CircleHelp, Info, UserRound } from "lucide-react";


import {
  bewaarContact,
  bewaarDieren,
  dienIn,
  haalOpgave,
  haalStand,
  haalVestiging,
  haalVragenlijst,
  zoekAdres,
  openAanvraag,
  uploadDocument,
  type AfstandContact,
  type AfstandOpgave,
  type AfstandStart,
  type AfstandVraag,
} from "../../afstand/afstandApi";
import { dierenUitOpgave, nieuwDier, type Dier } from "../../afstand/afstandDieren";
import { fotosVanDier, overigeBestanden, useVerstuurdeBijlagen } from "../../afstand/afstandBijlagen";
import { AfstandDierenStap } from "./AfstandDierenStap";
import { AfstandDocumentenStap, type Bijlagen } from "./AfstandDocumentenStap";
import type { Boekje } from "./AfstandEntingenboekje";
import { AfstandDossier } from "./AfstandDossier";
import { AfstandAfgesloten, standTekst } from "./AfstandStand";
import { AfstandIndienenStap } from "./AfstandIndienenStap";
import { AfstandMerk } from "./AfstandMerk";

/**
 * De aanvraag zelf: vier stappen links, de stap waar je nu bent rechts.
 *
 * <p><b>Vier stappen en niet vijf.</b> De afstandsvragen krijgen géén eigen hoofdscherm — die worden
 * per dier in een venster afgerond, want ze horen bij dát dier. Een vijfde stap "alle vragen van
 * alle dieren" zou een lijst worden waarin niemand meer ziet welk antwoord bij welk dier hoort.
 *
 * <p><b>Eén aanvraag per e-mailadres.</b> Wie twee dieren wegdoet, doet dat in één aanvraag — niet in
 * twee los van elkaar die het asiel daarna aan elkaar moet knopen. Daarom staan de dieren binnen de
 * aanvraag, en sluit de toegang zodra het asiel de aanvraag heeft afgehandeld.
 *
 * <p><b>De stappen die nog komen blijven zichtbaar.</b> Wie hier zit wil weten waaraan hij begint. Een
 * wizard die pas bij stap drie laat zien dat er nog documenten gevraagd worden, laat iemand
 * halverwege stranden.
 *
 * <p><b>Niets gaat weg voor hij het zelf verstuurt.</b> Dat staat er met zoveel woorden, en het klopt
 * ook: het asiel ziet de aanvraag pas na "Aanvraag indienen".
 */

const STAPPEN = [
  { sleutel: "gegevens", titel: "Uw gegevens", uitleg: "Contact- en adresgegevens" },
  { sleutel: "dieren", titel: "Uw dieren", uitleg: "Gegevens en vragen per dier" },
  { sleutel: "documenten", titel: "Documenten", uitleg: "Bijlagen en bewijsstukken" },
  { sleutel: "indienen", titel: "Controleren & indienen", uitleg: "Uw aanvraag versturen" },
] as const;

export type AanvraagStap = (typeof STAPPEN)[number]["sleutel"];

/** Wat er in de kop staat; per stap een eigen titel en uitleg, zoals in de mockups. */
const KOPPEN: Record<AanvraagStap, { titel: string; tekst: string }> = {
  gegevens: {
    titel: "Uw afstandsaanvraag",
    tekst: "U wilt een of meerdere dieren aanmelden voor afstand bij ons asiel. We begeleiden u stap"
      + " voor stap. We beginnen met uw gegevens; de vragen over uw dieren volgen daarna.",
  },
  dieren: {
    titel: "Uw dieren",
    tekst: "Voeg één of meerdere dieren toe. Beantwoord voor ieder dier afzonderlijk de"
      + " afstandsvragen en voeg de benodigde gegevens toe.",
  },
  documenten: {
    titel: "Documenten uploaden",
    tekst: "Voeg aanvullende documenten toe voor uw dieren. De bestanden zijn per dier opgeslagen.",
  },
  indienen: {
    titel: "Uw aanvraag controleren",
    tekst: "Controleer de gegevens van uzelf en van de aangemelde dieren voordat u de aanvraag"
      + " verstuurt.",
  },
};

export function AfstandAanvraag({ start, opvangSleutel, token, email, contact, onContact }: {
  start: AfstandStart | null;
  opvangSleutel: string;
  /** De sessie; elke schrijfactie draagt hem mee. */
  token: string;
  /** Het adres waarmee is ingelogd. Dat staat vast: het is zojuist met een code bewezen. */
  email: string;
  contact: AfstandContact | null;
  onContact: (c: AfstandContact) => void;
}) {
  const [stap, setStap] = useState<AanvraagStap>("gegevens");
  const [gedaan, setGedaan] = useState<AanvraagStap[]>([]);
  const [dieren, setDieren] = useState<Dier[]>(() => [nieuwDier(1)]);
  const [bijlagen, setBijlagen] = useState<Bijlagen>({});
  // De entingenboekjes staan hier en niet in de dierenstap: ze moeten een stapwissel overleven.
  const [boekjes, setBoekjes] = useState<Record<string, Boekje>>({});
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [ingediend, setIngediend] = useState(false);
  // De locatie die deze aanvraag krijgt, gezien de postcode van de eigenaar. Vooraf opgehaald zodat
  // de controlestap dezelfde naam noemt als de bevestiging erna.
  const [locatie, setLocatie] = useState<string | null>(null);
  /*
   * Het dossier van een ingediende aanvraag, en welk onderdeel hij ervan openklapte.
   *
   * Na indienen is het dossier wat hij ziet -- niet het formulier opnieuw vanaf stap 1. Klikt hij op
   * een potlood, dan opent dat ene onderdeel; opslaan brengt hem terug naar het dossier.
   */
  const [opgave, setOpgave] = useState<AfstandOpgave | null>(null);
  const [bewerkt, setBewerkt] = useState<AanvraagStap | null>(null);
  const [bewaren, setBewaren] = useState(false);
  // Of de opgave al binnen was; alleen dán is een fout in deze keten een echte fout.
  const opgaveGeladen = useRef(false);
  /*
   * De stand van een aanvraag die er al was.
   *
   * Wie terugkomt met hetzelfde adres hoort zijn eigen aanvraag terug te zien en niet een leeg
   * formulier. Hij wordt bij het asiel opgehaald en niet hier bijgehouden: een kopie van een status
   * die daar verandert, loopt gegarandeerd uit de pas.
   */
  const [stand, setStand] = useState<{
    aanvraagId?: string;
    status: string;
    afgesloten: boolean;
    vestigingNaam: string | null;
    aanvullingGevraagd: boolean;
  } | null>(null);

  const adrespostcode = contact?.postcode ?? "";
  useEffect(() => {
    if (adrespostcode.trim() === "") {
      setLocatie(null);
      return;
    }
    let levend = true;
    void haalVestiging(opvangSleutel, adrespostcode)
      .then((naam) => { if (levend) setLocatie(naam); })
      // Geen naam is geen fout: dan noemt het scherm er geen, en het asiel kiest hem alsnog zelf.
      .catch(() => { if (levend) setLocatie(null); });
    return () => { levend = false; };
  }, [opvangSleutel, adrespostcode]);

  /*
   * Een aanvraag die er al was, terug in beeld.
   *
   * Niet alleen de stand maar ook de opgave zelf: wie terugkomt om aan te vullen stuurt bij het
   * opnieuw indienen de hele set dieren, en een leeg formulier zou die dan wissen. Hij hoort zijn
   * eigen gegevens terug te zien zoals hij ze opgaf.
   *
   * Mislukt het ophalen van de opgave, dan blijft de stand staan en kan hij alleen kijken; dat is
   * beter dan een formulier met half ingevulde gegevens waarvan hij denkt dat het klopt.
   */
  useEffect(() => {
    let levend = true;
    void haalStand(token)
      .then(async (s) => {
        if (!levend || !s) return;
        setStand(s);
        setIngediend(true);
        setStap("indienen");
        const opgehaald = await haalOpgave(s.aanvraagId, token).catch(() => null);
        if (!levend || !opgehaald) return;
        opgaveGeladen.current = true;
        setOpgave(opgehaald);
        if (opgehaald.contact) {
          onContact(opgehaald.contact);
        }
        const terug = dierenUitOpgave(opgehaald.dieren, opgehaald.antwoorden);
        if (terug.length > 0) {
          setDieren(terug);
          setGedaan(["gegevens", "dieren"]);
        }
      })
      // Geen stand is geen fout: dan is er nog geen aanvraag. Maar een fout ná het ophalen is dat
      // wel, en die hoort niet in dezelfde stilte te verdwijnen.
      .catch((e) => {
        if (levend && opgaveGeladen.current) {
          // eslint-disable-next-line no-console -- zichtbaar maken wat anders stil wegviel
          console.error("Afstandsportaal: de aanvraag kon niet worden geladen", e);
        }
      });
    return () => { levend = false; };
  }, [token]);

  /*
   * De vragenlijsten per diersoort, zodra ze nodig zijn.
   *
   * Per diersoort één keer ophalen en onthouden: het asiel bepaalt per soort welke vragen het stelt,
   * en bij twee honden in dezelfde aanvraag hoeft dat niet twee keer over de lijn. Een kopie die
   * blijft staan is hier geen risico -- hij leeft net zo lang als dit tabblad.
   */
  /**
   * Per diersoort de vragen én de sleutel van de vragenlijst waar ze bij horen.
   *
   * <p><b>Die sleutel moet mee naar het asiel.</b> Zonder hem komt de aanvraag binnen zonder
   * vragenlijst, en dan staan de antwoorden er wel maar weet het dossier niet welke vraag bij welk
   * antwoord hoort -- het toont "0 van 0 beantwoord" terwijl de eigenaar alles invulde
   * (Maarten, 26-10-2026). Hij kwam al binnen bij het ophalen; hij werd alleen weggegooid.
   */
  const [vragenlijsten, setVragenlijsten] =
    useState<Record<string, { definitieId: string | null; vragen: AfstandVraag[] }>>({});
  const gekozenSoorten = dieren.map((d) => d.diersoort).filter(Boolean).join("|");

  useEffect(() => {
    let levend = true;
    for (const soort of new Set(gekozenSoorten.split("|").filter(Boolean))) {
      if (vragenlijsten[soort]) continue;
      void haalVragenlijst(opvangSleutel, soort)
        .then((lijst) => {
          // Geen lijst is een geldig antwoord: dan heeft het asiel voor deze soort niets ingericht.
          if (levend) {
            setVragenlijsten((e) => ({
              ...e,
              [soort]: { definitieId: lijst?.wizardDefinitieId ?? null, vragen: lijst?.vragen ?? [] },
            }));
          }
        })
        .catch(() => {
          if (levend) {
            setVragenlijsten((e) => ({ ...e, [soort]: { definitieId: null, vragen: [] } }));
          }
        });
    }
    return () => { levend = false; };
  }, [gekozenSoorten, opvangSleutel, vragenlijsten]);

  const vragenVan = useCallback(
    (diersoort: string) => vragenlijsten[diersoort]?.vragen ?? [],
    [vragenlijsten],
  );

  // De naam staat bij het asiel als één regel en hier in twee velden; het laatste woordendeel is de
  // achternaam, de rest de voornaam. Niet waterdicht bij "van der", maar beter dan een leeg veld --
  // en hij kan het aanpassen, want hij kijkt er toch naar.
  const [voornaam, setVoornaam] = useState(() => (contact?.naam ?? "").trim().split(/\s+/).slice(0, -1).join(" "));
  const [achternaam, setAchternaam] = useState(() => (contact?.naam ?? "").trim().split(/\s+/).slice(-1).join(""));
  const [telefoon, setTelefoon] = useState(contact?.telefoon ?? "");
  const [postcode, setPostcode] = useState(contact?.postcode ?? "");
  const [huisnummer, setHuisnummer] = useState(contact?.huisnummer ?? "");
  const [toevoeging, setToevoeging] = useState("");
  const [straat, setStraat] = useState(contact?.straat ?? "");
  const [woonplaats, setWoonplaats] = useState(contact?.woonplaats ?? "");

  /*
   * Gegevens die ná het opbouwen van dit scherm binnenkomen, alsnog invullen.
   *
   * Wie terugkomt om aan te vullen krijgt zijn opgave van het asiel, maar die is er pas een
   * ogenblik later -- en deze velden waren toen al leeg opgezet. Zonder dit stond hij voor een leeg
   * formulier, en opslaan wiste wat er lag (Maarten, 26-10-2026).
   *
   * Alleen lege velden worden gevuld: wat hij zelf al heeft ingetikt is leidend, ook als het asiel
   * iets anders weet.
   */
  useEffect(() => {
    if (!contact) return;
    const losseNaam = (contact.naam ?? "").trim().split(/\s+/).filter(Boolean);
    setVoornaam((v) => v || losseNaam.slice(0, -1).join(" "));
    setAchternaam((v) => v || losseNaam.slice(-1).join(""));
    setTelefoon((v) => v || contact.telefoon || "");
    setPostcode((v) => v || contact.postcode || "");
    setHuisnummer((v) => v || contact.huisnummer || "");
    setStraat((v) => v || contact.straat || "");
    setWoonplaats((v) => v || contact.woonplaats || "");
  }, [contact]);
  const [adresBezig, setAdresBezig] = useState(false);
  const [adresOnbekend, setAdresOnbekend] = useState(false);

  /*
   * Straat en woonplaats erbij zoeken zodra postcode en huisnummer er staan.
   *
   * Zelf intikken levert "Hogenhof", "hogenhof" en "Hogen hof" op, en dat moet bij het inboeken
   * alsnog rechtgezet worden. De velden blijven wel gewoon te overschrijven: een postbusadres of
   * een nieuwbouwstraat kent de opzoeker niet, en dan moet je er niet aan vastzitten.
   */
  useEffect(() => {
    const pc = postcode.replace(/\s/g, "");
    if (pc.length < 6 || huisnummer.trim() === "") {
      return;
    }
    let levend = true;
    setAdresBezig(true);
    setAdresOnbekend(false);
    const wachten = setTimeout(() => {
      void zoekAdres(opvangSleutel, postcode, huisnummer, toevoeging)
        .then((a) => {
          if (!levend) return;
          if (a.straat) setStraat(a.straat);
          if (a.woonplaats) setWoonplaats(a.woonplaats);
          setAdresOnbekend(!a.straat && !a.woonplaats);
        })
        .catch(() => { if (levend) setAdresOnbekend(true); })
        .finally(() => { if (levend) setAdresBezig(false); });
      // Even wachten met zoeken: anders gaat er een vraag de deur uit bij elke ingetikte letter
      // van het huisnummer.
    }, 400);
    return () => { levend = false; clearTimeout(wachten); setAdresBezig(false); };
  }, [postcode, huisnummer, toevoeging, opvangSleutel]);

  const gegevensCompleet = [voornaam, achternaam, telefoon, postcode, huisnummer, straat, woonplaats]
    .every((v) => v.trim() !== "");

  const nu = STAPPEN.findIndex((s) => s.sleutel === stap);
  const verst = Math.max(nu, ...gedaan.map((s) => STAPPEN.findIndex((x) => x.sleutel === s) + 1));

  /**
   * De contactgegevens zoals ze nu in de velden staan.
   *
   * <p>Als functie en niet als state: wie dit opslaat wil wat er nú staat. De gedeelde `contact`
   * wordt pas bijgewerkt nadat React de wijziging verwerkt heeft, en die een tel later uitlezen
   * leverde de vorige waarde op -- een gewijzigd telefoonnummer kwam zo nooit bij het asiel aan
   * (Maarten, 26-10-2026).
   */
  const huidigContact = (): AfstandContact => ({
    naam: `${voornaam.trim()} ${achternaam.trim()}`.trim(),
    email,
    telefoon: telefoon.trim(),
    straat: straat.trim(),
    huisnummer: `${huisnummer.trim()}${toevoeging.trim() ? ` ${toevoeging.trim()}` : ""}`,
    postcode: postcode.trim(),
    woonplaats: woonplaats.trim(),
  });

  const bewaarGegevens = () => {
    onContact(huidigContact());
    setGedaan((e) => (e.includes("gegevens") ? e : [...e, "gegevens"]));
    setStap("dieren");
  };

  /**
   * De hele aanvraag in één keer naar het asiel.
   *
   * <p>Pas hier gaat er iets de deur uit. Tot dit moment staat alles in dit tabblad: openen, de
   * dieren wegschrijven, de bijlagen en het indienen gebeuren achter elkaar, zodat er geen halve
   * aanvraag bij het asiel kan belanden waar niemand iets mee kan.
   *
   * <p>Gaat er onderweg iets mis, dan blijft alles staan zoals het is en kan de eigenaar het gewoon
   * nog eens proberen -- zijn antwoorden zijn niet weg.
   */
  /**
   * De vragenlijst die aan deze aanvraag hangt.
   *
   * <p>Een aanvraag draagt er één, ook als er dieren van verschillende soorten in zitten; we nemen de
   * eerste die er een heeft. Dat is geen keuze maar wat de administratie toelaat -- en beter dan geen
   * enkele, want zonder vragenlijst zijn alle antwoorden in het dossier onleesbaar.
   */
  const vragenlijstVanAanvraag = (): string | null => {
    for (const dier of dieren) {
      const id = vragenlijsten[dier.diersoort]?.definitieId;
      if (id) {
        return id;
      }
    }
    return null;
  };

  /**
   * Een gewijzigd onderdeel van een ingediende aanvraag wegschrijven.
   *
   * <p>Meteen naar het asiel, niet pas bij een nieuwe indiening: de aanvraag ligt daar al, en wat hij
   * hier verbetert hoort er binnen een tel ook te staan. De dieren gaan als hele set -- dat is hoe
   * het asiel een momentopname ontvangt.
   */
  const bewaarWijziging = async () => {
    const aanvraagId = stand?.aanvraagId;
    if (!aanvraagId) return;
    setBewaren(true);
    setFout(null);
    try {
      if (bewerkt === "gegevens") {
        const nieuw = huidigContact();
        await bewaarContact(aanvraagId, token, nieuw);
        onContact(nieuw);
      }
      if (bewerkt === "dieren") {
        await bewaarDieren(aanvraagId, token, dieren.map(naarOpgave));
      }
      /*
       * Wachtende bijlagen gaan altijd mee, welk onderdeel je ook opsloeg.
       *
       * Foto's van een dier voeg je toe bij dat dier, niet in de documentenstap. Alleen daar
       * uploaden betekende dat een foto bij het dier nooit verstuurd werd: hij stond op het scherm
       * en verdween bij het opslaan (Maarten, 27-10-2026).
       */
      for (const [sleutel, stapel] of Object.entries(bijlagen)) {
        if (stapel.length === 0) continue;
        const scheiding = sleutel.indexOf(":");
        const dierId = scheiding < 0 ? sleutel : sleutel.slice(0, scheiding);
        const soort = scheiding < 0 ? undefined : sleutel.slice(scheiding + 1);
        const volgnummer = dieren.findIndex((d) => d.id === dierId);
        for (const bijlage of stapel) {
          await uploadDocument(aanvraagId, token, bijlage.bestand,
            volgnummer < 0 ? undefined : volgnummer, soort);
        }
      }
      setBijlagen({});
      // Opnieuw ophalen in plaats van de wijziging hier naspelen: dan toont het dossier wat er bij
      // het asiel staat, en niet wat wij dachten te hebben verstuurd.
      const opnieuw = await haalOpgave(aanvraagId, token).catch(() => null);
      if (opnieuw) setOpgave(opnieuw);
      setBewerkt(null);
      setStap("indienen");
    } catch {
      setFout("Het opslaan is niet gelukt. Uw wijziging staat er nog; probeer het zo nog eens.");
    } finally {
      setBewaren(false);
    }
  };

  /**
   * Eén dier in de vorm die het asiel verwacht.
   *
   * <p>Op één plek, want hij gaat twee kanten op: bij het indienen en bij het wijzigen van een
   * aanvraag die er al ligt. Twee kopieën zouden na de eerste aanpassing uit elkaar lopen, en dan
   * komt er bij wijzigen stilletjes minder aan dan bij indienen.
   */
  const naarOpgave = (d: Dier) => ({
    naam: d.naam || null,
    diersoortId: d.diersoortId,
    // Het id en niet de naam: het asiel bewaart een verwijzing naar zijn eigen rassenlijst. Leeg
    // bij een vrij ingetikt ras; dat legt het asiel bij het inboeken alsnog op een echt ras.
    rasId: d.rasId,
    geslacht: d.geslacht || null,
    geboortedatum: d.geboortedatum || null,
    chipPasnummer: d.chipPasnummer || null,
    gecastreerd: null,
    antwoordenJson: JSON.stringify(d.antwoorden),
    // Alleen wat de eigenaar heeft nagelopen gaat mee; half ingevulde regels waar hij nog niet
    // naar gekeken heeft zouden bij het asiel als bevestigd overkomen.
    entingenJson: boekjes[d.id]?.gecontroleerd
      ? JSON.stringify(boekjes[d.id].entingen.map(({ id, ...rest }) => rest))
      : null,
  });

  const indienen = async () => {
    setBezig(true);
    setFout(null);
    try {
      const geopend = await openAanvraag(opvangSleutel, token, {
        naam: contact?.naam ?? "",
        email,
        telefoon: contact?.telefoon,
        straat: contact?.straat,
        huisnummer: contact?.huisnummer,
        postcode: contact?.postcode,
        woonplaats: contact?.woonplaats,
      }, vragenlijstVanAanvraag());
      const aanvraagId = geopend.aanvraagId;

      await bewaarDieren(aanvraagId, token, dieren.map(naarOpgave));

      // Eén voor één: een stapel gelijktijdige uploads naar hetzelfde dossier levert bij een
      // haperende verbinding half aangekomen bestanden op.
      //
      // De sleutel van een bijlage is `<dier-id>:<soort>`; daaruit volgt bij welk dier hij hoort. Het
      // asiel kent onze id's niet, dus gaat het volgnummer mee -- dezelfde volgorde als waarin de
      // dieren hierboven zijn weggeschreven.
      for (const [sleutel, stapel] of Object.entries(bijlagen)) {
        const scheiding = sleutel.indexOf(":");
        const dierId = scheiding < 0 ? sleutel : sleutel.slice(0, scheiding);
        const soort = scheiding < 0 ? undefined : sleutel.slice(scheiding + 1);
        const volgnummer = dieren.findIndex((d) => d.id === dierId);
        for (const bijlage of stapel) {
          await uploadDocument(aanvraagId, token, bijlage.bestand,
            volgnummer < 0 ? undefined : volgnummer, soort);
        }
      }

      // De bladzijden van de boekjes gaan er als bestand achteraan. De overgetikte regels staan al
      // bij het dier; de foto's blijven erbij zodat een dierenarts het boekje zelf kan nalezen in
      // plaats van op de overtikking te moeten vertrouwen.
      for (const [dierId, boekje] of Object.entries(boekjes)) {
        const volgnummer = dieren.findIndex((d) => d.id === dierId);
        for (const pagina of boekje.paginas) {
          await uploadDocument(aanvraagId, token, pagina.bestand,
            volgnummer < 0 ? undefined : volgnummer, "ENTINGENBOEKJE");
        }
      }

      await dienIn(aanvraagId, token);
      // De locatie die hem kreeg erbij, zodat de bevestiging kan zeggen wie er contact opneemt.
      setStand({ aanvraagId, status: "NIEUW", afgesloten: false,
                 vestigingNaam: geopend.vestigingNaam, aanvullingGevraagd: false });
      // En meteen zijn dossier ophalen. Zonder dit bleef het scherm op "Bezig met ophalen..." staan:
      // de opgave wordt anders alleen bij het openen van de pagina gehaald, en die stap is net
      // geweest (Maarten, 27-10-2026).
      const ingediendeOpgave = await haalOpgave(aanvraagId, token).catch(() => null);
      if (ingediendeOpgave) setOpgave(ingediendeOpgave);
      setIngediend(true);
    } catch {
      setFout("Het indienen is niet gelukt. Uw gegevens staan er nog; probeer het zo nog eens.");
    } finally {
      setBezig(false);
    }
  };

  /*
   * Wat er al bij het asiel ligt.
   *
   * De opgave is de aanleiding om opnieuw te kijken: die wordt na elk opslaan en na het indienen
   * verversd, en precies dan kan er een bijlage bij gekomen zijn.
   */
  const verstuurd = useVerstuurdeBijlagen(stand?.aanvraagId, token, opgave);
  const verstuurdeFotos = (dierId: string) =>
    fotosVanDier(verstuurd, dieren.findIndex((d) => d.id === dierId));

  const kop = KOPPEN[stap];

  return (
    <div className="afst afst--aanvraag">
      <header className="afst__balk">
        <AfstandMerk opvangSleutel={opvangSleutel} naam={start?.opvangNaam ?? ""} />
        <div className="afst__balkrechts">
          <a className="afst__hulp" href="#hulp"><CircleHelp size={18} aria-hidden="true" /> Hulp nodig?</a>
          <span className="afst__wie">
            <span className="afst__wieicoon" aria-hidden="true"><UserRound size={16} /></span>
            {email}
          </span>
        </div>
      </header>

      <div className="afst__werkblad">
        <nav className="afst__stappen" aria-label="Stappen van uw aanvraag">
          <p className="afst__stappenkop">Uw afstandsaanvraag</p>
          <ol>
            {STAPPEN.map((s, i) => {
              // Ingediend betekent dat alles af is; dan hoort elke stap een vinkje te hebben,
              // ook die hij in dit tabblad niet zelf doorliep.
              const klaar = ingediend || gedaan.includes(s.sleutel);
              return (
                <li key={s.sleutel}>
                  <button
                    type="button"
                    className={`afst__stap${s.sleutel === stap ? " is-nu" : ""}${klaar ? " is-klaar" : ""}`}
                    /*
                     * Vooruit springen kan niet zolang je nog invult: elke stap leunt op wat de
                     * vorige heeft opgeleverd.
                     *
                     * Na het indienen is dat anders: dan staat alles er en wil je er gewoon
                     * doorheen kunnen kijken (Maarten, 27-10-2026). Klikken opent dan hetzelfde
                     * onderdeel als het potlood op het dossier; de laatste stap brengt je terug
                     * naar het dossier zelf.
                     */
                    disabled={!ingediend && i > verst}
                    onClick={() => {
                      if (!ingediend) {
                        setStap(s.sleutel);
                        return;
                      }
                      if (s.sleutel === "indienen") {
                        setBewerkt(null);
                        setStap("indienen");
                        return;
                      }
                      if (s.sleutel === "dieren" && opgave) {
                        setDieren(dierenUitOpgave(opgave.dieren, opgave.antwoorden));
                      }
                      setBewerkt(s.sleutel);
                      setStap(s.sleutel);
                    }}
                  >
                    <span className="afst__stapnr" aria-hidden="true">
                      {klaar && s.sleutel !== stap ? <Check size={15} /> : i + 1}
                    </span>
                    <span className="afst__staptekst">
                      <strong>{s.titel}</strong>
                      {s.uitleg}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>

          <aside className="afst__zijnotitie">
            <strong>Eén actieve aanvraag per e-mailadres</strong>
            U kunt binnen deze aanvraag meerdere dieren aanmelden. Na afhandeling wordt de toegang
            afgesloten.
          </aside>
        </nav>

        <main className="afst__inhoud">
          <section className="afst__hero">
            <p className="afst__eyebrow">Afstandsportaal</p>
            <h1>{kop.titel}</h1>
            <p>{kop.tekst}</p>
            <AfstandHeroBand />
          </section>

          <div className="afst__stapkop">
            <span className="afst__stapteller">Stap {nu + 1} van {STAPPEN.length}</span>
            <h2>{STAPPEN[nu].titel}</h2>
            <span className="afst__stapstand">
              {/* Bij een ingediende aanvraag is "nog niet ingediend" onwaar, en "concept" ook: hij
                  wijzigt iets dat al bij het asiel ligt. */}
              {bewerkt != null ? "Uw wijziging gaat rechtstreeks naar het asiel"
                : stap === "gegevens" ? "Uw aanvraag is nog niet ingediend"
                : "Concept wordt tussentijds opgeslagen"}
            </span>
          </div>

          {stap === "gegevens" && (
            <section className="afst__paneel">
              <h3>Contactgegevens</h3>
              <p className="afst__paneeltekst">
                Vul uw gegevens in zodat het asiel u kan bereiken over de afstandsaanvraag. Alleen uw
                e-mailadres is al bekend.
              </p>

              <div className="afst__raster">
                <label className="afst__veld afst__veld--half">
                  <span>E-mailadres</span>
                  {/* Vastgezet: dit adres is zojuist met een code bewezen. Het hier laten wijzigen
                      zou die bewijslast meteen weggooien. */}
                  <span className="afst__invoer afst__invoer--vast">
                    <input type="email" value={email} readOnly />
                    <span className="afst__vink">Geverifieerd</span>
                  </span>
                </label>
                <Veld breed label="Telefoonnummer *" waarde={telefoon} zet={setTelefoon}
                      hint="Bijv. 06 1234 5678" />
                <Veld breed label="Voornaam *" waarde={voornaam} zet={setVoornaam} hint="Uw voornaam" />
                <Veld breed label="Achternaam *" waarde={achternaam} zet={setAchternaam} hint="Uw achternaam" />
                <Veld label="Postcode *" waarde={postcode} zet={setPostcode} hint="1234 AB" />
                <Veld label="Huisnummer *" waarde={huisnummer} zet={setHuisnummer} hint="Nummer" />
                <Veld label="Toevoeging" waarde={toevoeging} zet={setToevoeging} hint="Optioneel" />
                <Veld breed label="Straat *" waarde={straat} zet={setStraat}
                      hint={adresBezig ? "Zoeken…" : "Uw straatnaam"} />
                <Veld breed label="Woonplaats *" waarde={woonplaats} zet={setWoonplaats}
                      hint={adresBezig ? "Zoeken…" : "Uw woonplaats"} />
              </div>

              {adresOnbekend && (
                <p className="afst__melding">
                  We vinden deze postcode en huisnummer niet. Vul straat en woonplaats hierboven zelf
                  in; dat kan prima.
                </p>
              )}

              <p className="afst__tip">
                <span className="afst__tipicoon" aria-hidden="true"><Info size={15} /></span>
                Hierna kunt u uw dier(en) toevoegen. Voor ieder dier vult u afzonderlijk de benodigde
                gegevens en afstandsvragen in.
              </p>
            </section>
          )}

          {stap === "dieren" && (
            <AfstandDierenStap
              dieren={dieren}
              zetDieren={setDieren}
              diersoorten={start?.diersoorten ?? []}
              vragenVan={vragenVan}
              boekjes={boekjes}
              zetBoekjes={setBoekjes}
              opvangSleutel={opvangSleutel}
              bijlagen={bijlagen}
              zetBijlagen={setBijlagen}
              verstuurdeFotos={verstuurdeFotos}
            />
          )}

          {stap === "documenten" && (
            <AfstandDocumentenStap dieren={dieren} bijlagen={bijlagen} zetBijlagen={setBijlagen} />
          )}

          {stap === "indienen" && !ingediend && (
            <AfstandIndienenStap
              contact={contact}
              dieren={dieren}
              bijlagen={bijlagen}
              vragenVan={vragenVan}
              opvangNaam={locatie ?? start?.opvangNaam ?? ""}
              bezig={bezig}
              onIndienen={() => void indienen()}
            />
          )}

          {stap === "indienen" && ingediend && stand?.afgesloten && (
            <AfstandAfgesloten onOpnieuw={() => globalThis.location?.reload()} />
          )}

          {/*
            * Ingediend: het dossier, niet het formulier.
            *
            * Wie terugkomt hoort te zien wat hij heeft doorgegeven, met per onderdeel een potlood --
            * en niet opnieuw vanaf stap 1 te moeten lopen voor één telefoonnummer.
            */}
          {stap === "indienen" && ingediend && !stand?.afgesloten && bewerkt == null && (
            <AfstandDossier
              opgave={opgave}
              fotosVan={(plek) => fotosVanDier(verstuurd, plek)}
              overigeBestanden={overigeBestanden(verstuurd)}
              status={standTekst(stand?.status, stand?.aanvullingGevraagd)}
              afgesloten={Boolean(stand?.afgesloten)}
              onBewerk={(onderdeel) => {
                /*
                 * De dieren komen hier uit het dossier dat hij op dit moment vóór zich heeft.
                 *
                 * Niet uit een state die bij het laden van de pagina gevuld had moeten worden: dat
                 * hing van de volgorde van twee aanroepen af, en ging mis -- dan opende het potlood
                 * een leeg "Nieuw dier" en was zijn dier bij het opslaan verdwenen
                 * (Maarten, 26-10-2026). Wat hij ziet is wat hij bewerkt; daar valt niets tussen.
                 */
                if (onderdeel === "dieren" && opgave) {
                  setDieren(dierenUitOpgave(opgave.dieren, opgave.antwoorden));
                }
                setBewerkt(onderdeel);
                setStap(onderdeel);
              }}
              uitleg={stand?.aanvullingGevraagd ? (
                <p className="afst__melding afst__melding--let-op">
                  Het asiel heeft nog iets van u nodig. Wijzig hieronder het onderdeel dat het betreft.
                </p>
              ) : undefined}
            />
          )}

          {fout && <p className="afst__melding afst__melding--fout" role="alert">{fout}</p>}

          {/*
            * Bewerken vanuit het dossier: geen stappenvoet maar opslaan of terug.
            *
            * De aanvraag ligt al bij het asiel; hij verbetert één onderdeel en gaat terug naar zijn
            * dossier. Hem door de resterende stappen laten lopen zou suggereren dat hij opnieuw moet
            * indienen, en dat hoeft niet.
            */}
          {bewerkt != null && (
            <footer className="afst__voet">
              <button type="button" className="afst__knop afst__knop--rustig" disabled={bewaren}
                      onClick={() => { setBewerkt(null); setStap("indienen"); }}>
                <ArrowLeft size={18} aria-hidden="true" />
                Terug zonder opslaan
              </button>
              <button type="button" className="afst__knop" disabled={bewaren}
                      onClick={() => void bewaarWijziging()}>
                {bewaren ? "Bezig…" : "Opslaan en terug naar mijn aanvraag"}
                <ArrowRight size={18} aria-hidden="true" />
              </button>
            </footer>
          )}

          {bewerkt == null && !(stap === "indienen" && ingediend) && (
          <footer className="afst__voet">
            {nu > 0 && (
              <button type="button" className="afst__knop afst__knop--rustig"
                      onClick={() => setStap(STAPPEN[nu - 1].sleutel)}>
                <ArrowLeft size={18} aria-hidden="true" />
                Vorige stap
              </button>
            )}
            {nu === 0 && (
              <span className="afst__stapstand" style={{ marginLeft: 0, marginRight: "auto" }}>
                Uw aanvraag wordt pas verstuurd wanneer u deze zelf indient.
              </span>
            )}
            {stap !== "indienen" && (
            <button type="button" className="afst__knop"
                    disabled={stap === "gegevens" && !gegevensCompleet}
                    onClick={() => {
                      if (stap === "gegevens") { bewaarGegevens(); return; }
                      setGedaan((e) => (e.includes(stap) ? e : [...e, stap]));
                      if (nu < STAPPEN.length - 1) setStap(STAPPEN[nu + 1].sleutel);
                    }}>
              {stap === "gegevens" ? "Opslaan en verder"
                : stap === "dieren" ? "Verder naar documenten"
                : "Verder naar controleren"}
              <ArrowRight size={18} aria-hidden="true" />
            </button>
            )}
          </footer>
          )}
        </main>
      </div>
    </div>
  );
}

/** Eén tekstveld in het raster; `breed` is de helft van de rij, anders een derde. */
function Veld({ label, waarde, zet, hint, breed }: {
  label: string;
  waarde: string;
  zet: (v: string) => void;
  hint?: string;
  breed?: boolean;
}) {
  return (
    <label className={`afst__veld ${breed ? "afst__veld--half" : "afst__veld--derde"}`}>
      <span>{label}</span>
      <span className="afst__invoer">
        <input value={waarde} placeholder={hint} onChange={(e) => zet(e.target.value)} />
      </span>
    </label>
  );
}

/** De band rechts in de kop. Versiering: laadt hij niet, dan blijft er gewoon tekst staan. */
function AfstandHeroBand() {
  const [zichtbaar, setZichtbaar] = useState(true);
  if (!zichtbaar) {
    return null;
  }
  return (
    <div className="afst__herobeeld">
      <img src="/afstand/hero.png" alt="" onError={() => setZichtbaar(false)} />
    </div>
  );
}
